import { Link } from "react-router-dom";
import { Video, PhoneCall, Building2, CheckCircle2, Info, CalendarCheck } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";
import { ONLINE_CONSULT_USES } from "@/data/content";

const OPTIONS = [
  {
    icon: Video,
    title: "Video Consultation",
    desc: "A face-to-face private consultation with Dr. Ankur Lal over video — from anywhere.",
    type: "online",
  },
  {
    icon: PhoneCall,
    title: "Phone Consultation",
    desc: "Prefer not to be on camera? A phone consultation offers the same expert guidance, discreetly.",
    type: "online",
  },
  {
    icon: Building2,
    title: "Clinic Consultation",
    desc: "An in-person consultation at the clinic in Delhi — private, unhurried, by appointment.",
    type: "clinic",
  },
];

const OnlineConsultation = () => (
  <>
    <SEO
      title="Online STD Consultation with Dr. Ankur Lal | Confidential STD Doctor Online — Delhi"
      description="Private online STD consultation with Dr. Ankur Lal, STD & Sexual Health Specialist (Ex-AIIMS New Delhi). Video & phone consultations for STD/STI symptoms, HIV concerns, PEP/PrEP guidance and follow-ups."
      path="/online-consultation"
    />
    <PageHero
      eyebrow="Online STD Doctor Consultation"
      title="Online STD Consultation with Dr. Ankur Lal"
      intro="Private sexual-health consultation from the comfort of your home — confidential, professional and judgment-free."
    />

    <section className="py-14 sm:py-20" data-testid="online-uses">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>What It's For</Eyebrow>
          <SectionTitle className="mt-4">Online consultation can help with</SectionTitle>
        </Reveal>
        <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-3.5">
          {ONLINE_CONSULT_USES.map((u, i) => (
            <Reveal key={u} delay={(i % 6) * 0.04}>
              <div
                data-testid={`online-use-${i}`}
                className="h-full bg-white border border-slate-900/10 rounded-2xl px-5 py-4 flex items-center gap-3"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-800 shrink-0" />
                <span className="text-sm font-medium text-slate-800 leading-snug">{u}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-300/60 rounded-2xl p-5" data-testid="online-limitation-note">
            <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-950 leading-relaxed">
              Some conditions may require physical examination, laboratory testing or an
              in-person consultation. If that's the case for you, Dr. Ankur Lal will guide
              you clearly on the next step.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-14 sm:py-20 bg-white border-y border-slate-900/8" data-testid="booking-options">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>Choose Your Format</Eyebrow>
          <SectionTitle className="mt-4">Three Ways to Consult</SectionTitle>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {OPTIONS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="h-full bg-[#FAF9F6] border border-slate-900/10 rounded-3xl p-8 text-center flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(6,78,59,0.25)]">
                <span className="mx-auto w-12 h-12 rounded-2xl bg-emerald-900 flex items-center justify-center">
                  <o.icon className="w-5 h-5 text-emerald-100" />
                </span>
                <h3 className="mt-5 font-serif text-2xl font-semibold text-slate-950">{o.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{o.desc}</p>
                <Link
                  to={`/booking?type=${o.type}`}
                  data-testid={`option-book-${o.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all"
                >
                  <CalendarCheck className="w-4 h-4" /> Book Now
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBand
      title="Book Your Online Consultation"
      text="Confidential appointment in under a minute. Consult from the privacy of your home."
    />
  </>
);

export default OnlineConsultation;
